/**
 * `/` -- the Start view, the first thing a signed-in admin sees.
 *
 * ONE REQUEST (GET /admin-api/overview, D3) drives four cards -- Konto,
 * Tabelle, eBay, Letzte Jobs -- plus the "Cache aktualisieren" action
 * (task-8-brief.md Step 1). Billing state sits on the Konto card in addition
 * to Layout's chrome banner: this package's answer to the phase-4 finding
 * that POST /admin-api/actions/ebay-sync checks `features.ebay` and never
 * `tenant.status` (capabilities.ts's recorded discrepancy, E3) -- the state
 * is never a surprise even though it is not enforced server-side.
 *
 * DECIDES NOTHING ITSELF. `buildStartViewModel` (lib/startView.ts) turns the
 * query's response into everything the four cards need -- which branch each
 * card is in, every pill kind, every precomputed sentence -- and
 * `startRefreshCache` applies the one mutation's result. This file only
 * reads the model and switches on its discriminants (review I-2, fix round
 * 1: the JSX ternaries that used to compute these answers directly had zero
 * test coverage and all four of the reviewer's call-site mutations there
 * escaped the whole gate).
 *
 * DESIGN SPIKE: the four <article>s became a responsive card grid built from
 * `components/ui/card.tsx`. Every branch, every pill and every sentence is the
 * same model field it was; only the arrangement changed, and the two class
 * hooks the DOM suite reads (`.start-billing-line`, plus StatusPill's own
 * `data-kind`) are untouched.
 */
import { useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, ArrowRight, Building2, RefreshCw, Store, Table2 } from 'lucide-react';
import { ADMIN_API } from '../lib/api';
import { guardedAction } from '../lib/gate';
import { t } from '../lib/i18n';
import { isPlainLeftClick, pathFor } from '../lib/router';
import { cn } from '../lib/utils';
import { overviewQueryOptions } from '../queries';
import {
  buildStartViewModel,
  startRefreshCache,
  type EbayCardModel,
  type RefreshMessageKind,
  type SheetCardModel,
} from '../lib/startView';
import { ErrorNotice } from '../components/ErrorNotice';
import { JobLine } from '../components/JobLine';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardStat, CardTitle } from '../components/ui/card';
import { PageBody, PageHeader } from '../components/ui/page';
import { useRoute } from '../Router';

type LinkClickHandler = (event: MouseEvent<HTMLAnchorElement>, path: string) => void;

/** The card grid's shared head: an icon chip, the card's name, an optional
 *  trailing node (a pill). No decision of its own. */
function SectionHead({ icon, title, trailing }: { icon: ReactNode; title: string; trailing?: ReactNode }) {
  return (
    <CardHeader>
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          className="grid size-7 shrink-0 place-items-center rounded-[0.45rem] border border-line bg-surface-2 text-muted [&_svg]:size-3.5"
        >
          {icon}
        </span>
        <CardTitle className="truncate">{title}</CardTitle>
      </div>
      {trailing === undefined ? null : <div className="shrink-0">{trailing}</div>}
    </CardHeader>
  );
}

/** An in-app link that reads as a call to action rather than as body text. */
function CardLink({ href, label, onLinkClick }: { href: string; label: string; onLinkClick: LinkClickHandler }) {
  return (
    <a
      href={href}
      onClick={(event) => onLinkClick(event, href)}
      className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-accent no-underline hover:gap-2 hover:underline"
    >
      {label}
      <ArrowRight aria-hidden="true" className="size-3.5" />
    </a>
  );
}

/** No decision of its own: `sheet` is already the fully-resolved
 *  `SheetCardModel` (lib/startView.ts's `sheetCardModel`), this only
 *  switches on its `kind`. */
function SheetCard({ sheet, onLinkClick }: { sheet: SheetCardModel; onLinkClick: LinkClickHandler }) {
  return (
    <Card className="start-card start-card-sheet">
      <SectionHead
        icon={<Table2 />}
        title={t('start.card.sheet')}
        trailing={sheet.kind === 'never' ? undefined : <StatusPill kind={sheet.pill.kind} label={sheet.pill.label} />}
      />
      <CardContent className="space-y-2">
        {sheet.kind === 'never' ? (
          <>
            <p className="text-[0.8125rem] text-muted">{t('start.sheet.notConnected')}</p>
            <CardLink href={pathFor('sheet')} label={t('start.sheet.connectLink')} onLinkClick={onLinkClick} />
          </>
        ) : (
          <>
            {sheet.validatedLine === null ? null : (
              <p className="start-sheet-validated text-xs text-muted">{sheet.validatedLine}</p>
            )}
            {sheet.serviceAccount ? (
              <p className="start-sheet-service-account text-xs text-muted">{t('start.sheet.serviceAccount')}</p>
            ) : null}
            {sheet.errorLine === null ? null : (
              <p className="start-sheet-error text-[0.8125rem] text-danger">{sheet.errorLine}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * `'never'` (no row at all) and `'inactive'` (a row exists but is revoked or
 * erroring -- review I-1) both render the SAME reconnect link a
 * never-connected tenant gets; `'inactive'` additionally shows the real
 * status so a revoked connection is never confused with one that was never
 * made. Only `'active'` shows connection detail (scopes, connectedAt).
 */
function EbayCard({ ebay, onLinkClick }: { ebay: EbayCardModel; onLinkClick: LinkClickHandler }) {
  return (
    <Card className="start-card start-card-ebay">
      <SectionHead
        icon={<Store />}
        title={t('start.card.ebay')}
        trailing={ebay.kind === 'never' ? undefined : <StatusPill kind={ebay.pill.kind} label={ebay.pill.label} />}
      />
      <CardContent className="space-y-2">
        {ebay.kind === 'active' ? (
          <>
            <p className="text-[0.8125rem] text-muted">{ebay.connectedAtLine}</p>
            <p className="start-ebay-scopes-title pt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.055em] text-muted">
              {t('start.ebay.scopesTitle')}
            </p>
            <ul className="start-ebay-scopes">
              {ebay.scopes.map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
          </>
        ) : (
          <>
            {ebay.kind === 'inactive' ? (
              <p className="start-ebay-note text-[0.8125rem] text-muted">{ebay.note}</p>
            ) : (
              <p className="text-[0.8125rem] text-muted">{t('start.ebay.notConnected')}</p>
            )}
            <CardLink href={pathFor('ebay')} label={t('start.ebay.connectLink')} onLinkClick={onLinkClick} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function Start() {
  const overview = useQuery(overviewQueryOptions());
  const queryClient = useQueryClient();
  const { navigate } = useRoute();

  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [refreshKind, setRefreshKind] = useState<RefreshMessageKind>('ok');
  /**
   * THE SAME GATE `/jobs` USES, for the same action (final review I-2).
   * "Cache aktualisieren" is `refresh-cache`, reachable from this card and from
   * the actions panel on `/jobs`, and until this fix the two sites gave it two
   * different guarantees: a `useRef` there, `if (refreshPending) return;` here
   * -- which the review's M28 deleted outright without reddening any of the
   * 1096 tests, because React state has not re-rendered yet when the second
   * click of a double-click arrives. See lib/gate.ts.
   */
  const refreshGate = useRef<string | null>(null);

  // The same click-interception Layout.tsx's handleNav uses, through the same
  // function: a real href, intercepted only for a plain left click so it
  // becomes an in-app navigation instead of a full reload.
  function handleLink(event: MouseEvent<HTMLAnchorElement>, path: string) {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(path);
  }

  function handleRefreshCache() {
    guardedAction(refreshGate, 'refresh-cache', () => startRefreshCache({
      queryClient,
      runAction: ADMIN_API.runAction,
      setPending: setRefreshPending,
      setMessage: setRefreshMessage,
      setMessageKind: setRefreshKind,
    }));
  }

  if (overview.isPending) return <Spinner />;

  if (overview.isError) {
    return (
      <section className="start-view">
        <ErrorNotice error={overview.error} />
        <Button onClick={() => void overview.refetch()}>{t('blocking.retry')}</Button>
      </section>
    );
  }

  const model = buildStartViewModel(overview.data, Date.now());

  return (
    <section className="start-view">
      <PageBody>
        <PageHeader title={t('nav.start')} />

        {/* items-start so a short card (Tabelle) is not stretched to the
            height of a tall neighbour (eBay with three scopes listed). */}
        <div className="start-cards grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          {/* Konto -- the widest card, because the billing line lives on it.
              The status pill appears ONCE, in the field `start.account.status`
              names: a second copy in the card head would be the same fact
              rendered twice, and two nodes carrying one label is also how a
              `findByText` in the DOM suite stops being unambiguous. */}
          <Card className="start-card start-card-account lg:col-span-2">
            <SectionHead icon={<Building2 />} title={t('start.card.account')} />
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <CardStat label={t('start.account.name')} value={overview.data.tenant.name} />
                <CardStat label={t('start.account.plan')} value={overview.data.tenant.plan} />
                <CardStat
                  label={t('start.account.status')}
                  value={<StatusPill kind={model.tenant.pill.kind} label={model.tenant.pill.label} />}
                />
              </div>
              {model.tenant.billingLine === null ? null : (
                <p className="start-billing-line mt-4 rounded-field border border-warn/35 bg-warn-soft px-3 py-2 text-[0.8125rem] font-medium text-warn">
                  {model.tenant.billingLine}
                </p>
              )}
            </CardContent>
          </Card>

          <SheetCard sheet={model.sheet} onLinkClick={handleLink} />
          <EbayCard ebay={model.ebay} onLinkClick={handleLink} />

          <Card className="start-card start-card-jobs lg:col-span-2">
            <SectionHead icon={<Activity />} title={t('start.card.jobs')} />
            <CardContent>
              {model.jobs.length === 0 ? (
                <p className="jobs-empty">{t('start.jobs.empty')}</p>
              ) : (
                <ul className="start-jobs-list job-lines">
                  {model.jobs.map((job) => (
                    <JobLine key={job.id} job={job} />
                  ))}
                </ul>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line-soft pt-4">
                <Button onClick={handleRefreshCache} disabled={refreshPending}>
                  <RefreshCw aria-hidden="true" className={cn(refreshPending && 'animate-spin')} />
                  {t('start.actions.refreshCache')}
                </Button>
                {refreshMessage === null ? null : (
                  <p
                    className={cn(
                      'start-refresh-message text-[0.8125rem]',
                      refreshKind === 'ok' ? 'text-ok' : 'text-danger',
                    )}
                    role="status"
                    data-kind={refreshKind}
                  >
                    {refreshMessage}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </PageBody>
    </section>
  );
}
